import { useEffect, useState } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';

interface RocketSceneProps {
  hideOnHomeHero?: boolean;
}

export default function RocketScene({ hideOnHomeHero = false }: RocketSceneProps) {
  const { scrollYProgress } = useScroll();
  const [windowHeight, setWindowHeight] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    setWindowHeight(window.innerHeight);
    const handleResize = () => setWindowHeight(window.innerHeight);
    const handleScroll = () => setScrollY(window.scrollY);
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);
    
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  // Hide rocket on home page hero (above the fold)
  const isInHomeHero = hideOnHomeHero && scrollY < windowHeight * 0.8;

  // Transform scroll progress to rocket position with physics - constrain to visible range
  const y = useTransform(scrollYProgress, [0, 0.8], [0, -windowHeight * 0.5]);
  const smoothY = useSpring(y, { stiffness: 100, damping: 30 });
  
  // Add subtle mouse-following behavior
  const mouseX = useSpring(mousePosition.x, { stiffness: 150, damping: 30 });
  const mouseY = useSpring(mousePosition.y, { stiffness: 150, damping: 30 });
  const rotateFromMouse = useTransform(mouseX, [0, window.innerWidth], [-3, 3]);
  
  const rotate = useTransform(scrollYProgress, [0, 0.5, 1], [0, -5, 5]);
  const opacity = useTransform(scrollYProgress, [0, 0.1, 0.7, 0.85], [1, 1, 1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.2, 0.7, 0.85], [1, 1.1, 1.05, 0.8]);

  // Don't render if we're in the home hero section
  if (isInHomeHero) {
    return null;
  }

  return (
    <motion.div
      className="fixed right-8 bottom-8 z-40 pointer-events-none will-change-transform"
      style={{ 
        y: smoothY, 
        opacity, 
        scale,
      }}
    >
      <motion.svg
        width="80"
        height="80"
        viewBox="0 0 100 100"
        style={{ rotate: rotateFromMouse }}
        className="drop-shadow-[0_0_15px_rgba(59,130,246,0.5)]"
      >
        {/* Rocket Body with gradient */}
        <motion.path
          d="M50 10 L35 60 L50 55 L65 60 Z"
          fill="url(#rocketGradient)"
          stroke="#3b82f6"
          strokeWidth="2"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 2, ease: "easeInOut" }}
        />
        
        {/* Rocket Fins with shimmer */}
        <motion.path
          d="M35 60 L25 75 L35 70 Z"
          fill="#a855f7"
          animate={{
            fill: ['#a855f7', '#9333ea', '#a855f7'],
          }}
          transition={{ duration: 2, repeat: Infinity }}
        />
        <motion.path
          d="M65 60 L75 75 L65 70 Z"
          fill="#a855f7"
          animate={{
            fill: ['#a855f7', '#9333ea', '#a855f7'],
          }}
          transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
        />
        
        {/* Rocket Window with glow */}
        <circle
          cx="50"
          cy="30"
          r="8"
          fill="#1e293b"
          stroke="#3b82f6"
          strokeWidth="2"
        />
        <motion.circle
          cx="50"
          cy="30"
          r="4"
          fill="#3b82f6"
          animate={{
            opacity: [0.5, 1, 0.5],
          }}
          transition={{ duration: 1.5, repeat: Infinity }}
        />
        
        {/* Enhanced Rocket Flame with turbulence */}
        <motion.path
          d="M43 60 L50 85 L57 60"
          fill="url(#flameGradient)"
          animate={{
            d: [
              "M43 60 L50 85 L57 60",
              "M43 60 L48 90 L57 60",
              "M43 60 L52 88 L57 60",
              "M43 60 L50 85 L57 60",
            ],
          }}
          transition={{
            duration: 0.2,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        
        {/* Gradients */}
        <defs>
          <linearGradient id="rocketGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="50%" stopColor="#2563eb" />
            <stop offset="100%" stopColor="#1e40af" />
          </linearGradient>
          <linearGradient id="flameGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#f97316" />
            <stop offset="30%" stopColor="#fb923c" />
            <stop offset="60%" stopColor="#fbbf24" />
            <stop offset="100%" stopColor="#fde047" />
          </linearGradient>
        </defs>
      </motion.svg>
      
      {/* Enhanced Particle Trail */}
      <div className="absolute left-1/2 -translate-x-1/2 top-full">
        {[...Array(5)].map((_, i) => (
          <motion.div
            key={i}
            className="w-2 h-2 rounded-full bg-gradient-to-b from-launch-orange to-transparent mb-1"
            animate={{
              opacity: [0.8, 0],
              scale: [1, 0.3],
              y: [0, 25],
            }}
            transition={{
              duration: 1,
              repeat: Infinity,
              delay: i * 0.15,
              ease: "easeOut",
            }}
          />
        ))}
      </div>
      
      {/* Sparkles */}
      {[...Array(3)].map((_, i) => (
        <motion.div
          key={`sparkle-${i}`}
          className="absolute w-1 h-1 rounded-full bg-white"
          style={{
            left: `${20 + i * 20}%`,
            top: `${40 + i * 10}%`,
          }}
          animate={{
            opacity: [0, 1, 0],
            scale: [0, 1.5, 0],
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            delay: i * 0.5,
          }}
        />
      ))}
    </motion.div>
  );
}
